import { createSelector } from 'reselect';
import { RootState, UnitState } from '../model/state';
import { Alert, AlertStatus, Device, DeviceType, RealTimeDeviceData, Unit } from 'model';
import { flattenDate } from './helpers/flattenDate';
import _ from 'lodash';

/**
 * Direct selector to the state
 */
const selectUnit = () => (state: RootState) => state.unit;

/**
 * Other specific selectors
 */
const selectCurrentUnit = () => createSelector(
  selectUnit(),
  selectAreaTemperature(),
  selectIsDetectingWaterMap(),
  (unit: UnitState, areaTemperature: number | undefined, isDetectingWaterMap: { [deviceId: string]: boolean }) => {
    return unit.currentUnit && {
      ...unit.currentUnit,
      devices: unit.currentUnit?.devices?.map(dev => {
        return (dev.type === DeviceType.SHUTOFF) ? {
          ...dev,
          telemetry: {
            ...dev.telemetry,
            temp: {
              ...dev.telemetry.temp,
              value: areaTemperature || 0,
            }
          }
        } : {
          ...dev,
          isDetectingWater: isDetectingWaterMap[dev.id],
        };
      })
    }
  }
);

const selectCurrentMacAddress = () => createSelector(
  selectUnit(),
  (unit: UnitState) => unit.currentMacAddress
);

const selectCurrentDevice = () => createSelector(
  selectCurrentUnit(),
  selectCurrentMacAddress(),
  (currentUnit: Unit | undefined, macAddress: string | undefined) => {
    return currentUnit?.devices?.find(dev => dev.macAddress === macAddress);
  }
);

const selectHasMultipleDevices = () => createSelector(
  selectUnit(),
  (unit: UnitState) => (unit.currentUnit?.devices || []).length > 1
);

const selectRealtimeMap = () => createSelector(
  selectUnit(),
  selectAreaTemperature(),
  (unit: UnitState, areaTemperature: number | undefined) => {
    return _.chain(unit.realTimeData)
      .mapValues(device => ({
        ...device,
        telemetry: {
          ...device.telemetry,
          temp: {
            ...device.telemetry.temp,
            value: areaTemperature || 0
          }
        }
      }))
      .value();
  }
);

const selectCurrentRealtimeData = () => createSelector(
  selectUnit(),
  selectCurrentMacAddress(),
  selectAreaTemperature(),
  (unit: UnitState, macAddress: string | undefined, areaTemperature: number | undefined) => {

    const device = macAddress !== undefined ? unit.realTimeData[macAddress] : undefined;
    // Merge areaTemp into telemetry data
    if (device) {
      return {
        ...device,
        telemetry: {
          ...device.telemetry,
          temp: {
            ...device.telemetry.temp,
            value: areaTemperature || 0
          }
        }
      }
    }
    return undefined;
  }
);

const selectUpdatedTelemetryText = () => createSelector(
  selectCurrentDevice(),
  selectCurrentRealtimeData(),
  (device?: Device, realtime?: RealTimeDeviceData) => {
    if (!device) {
      return "";
    }
    if (device.type === DeviceType.DETECTOR) {
      return device.telemetry.updated ? flattenDate(device.telemetry.updated) : "";
    } else {
      return realtime?.telemetry?.updated ? flattenDate(realtime?.telemetry?.updated) : flattenDate(device.telemetry.updated);
    }
  }
);

const selectAlertList = () => createSelector(
  selectUnit(),
  (unit: UnitState) => unit.alerts.list
);

const selectIsLoadingAlert = () => createSelector(
  selectUnit(),
  (unit: UnitState) => unit.alerts.isLoading
);

const selectPendingAlerts = () => createSelector(
  selectUnit(),
  (unit: UnitState) => unit.pendingAlerts
);

const selectIsDetectingWaterMap = () => createSelector(
  selectPendingAlerts(),
  (pendingAlerts: Alert[]): { [deviceId: string]: boolean } => {
    return pendingAlerts.reduce((prev: { [deviceId: string]: boolean }, curr: Alert) => {
      const waterDetected = curr.alarm.id === 100 && curr.status === AlertStatus.TRIGGERED;
      return {
        ...prev,
        [curr.deviceId]: prev[curr.deviceId] ? prev[curr.deviceId] || waterDetected : waterDetected,
      }
    }, {});
  }
);

const selectPendingAlertsForCurrentDevice = () => createSelector(
  selectPendingAlerts(),
  selectCurrentDevice(),
  (alerts: Alert[], device: Device | undefined): Alert[] => {
    return device ? alerts.filter(a => a.deviceId === device.id) : [];
  }
);

const selectIsLoadingUnit = () => createSelector(
  selectUnit(),
  (unit: UnitState) => unit.isLoadingUnit
);

const selectAreaTemperature = () => createSelector(
  selectUnit(),
  (unit: UnitState) => unit.areaTemperature
);

export default selectUnit;
export {
  selectCurrentUnit,
  selectCurrentDevice,
  selectCurrentMacAddress,
  selectHasMultipleDevices,
  selectCurrentRealtimeData,
  selectUpdatedTelemetryText,
  selectRealtimeMap,
  selectAlertList,
  selectIsLoadingAlert,
  selectIsLoadingUnit,
  selectAreaTemperature,
  selectPendingAlertsForCurrentDevice
}